import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import UIStore from '../flux/stores/UIStore';
import AuthStore from '../flux/stores/AuthStore';
import ProjectStore from '../flux/stores/ProjectStore';
import Actions from '../flux/Actions';
import {
   type Project,
   type Scene,
   type Character,
   type Location,
   type Plot,
} from '../types/entities';
import appPackage from '../../package.json';

type FormState = Partial<Project & Scene & Character & Location & Plot> & {
   imageFile?: File | null;
   genre?: string;
};

type AppInfo = {
   name: string;
   version: string;
   packageVersion?: string;
   appId?: string;
   isPackaged?: boolean;
   platform?: string;
};

const packageMetadata = appPackage as {
   productName?: string;
   appId?: string;
   build?: {
      productName?: string;
      appId?: string;
      extraMetadata?: {
         productName?: string;
         appId?: string;
      };
   };
};
const packageBuild = packageMetadata.build;
const fallbackAppInfo: AppInfo = {
   name:
      packageMetadata.productName ||
      packageBuild?.productName ||
      packageBuild?.extraMetadata?.productName ||
      'Quill Studio',
   version: appPackage.version || 'Unknown',
   packageVersion: appPackage.version || 'Unknown',
   appId:
      packageMetadata.appId ||
      packageBuild?.appId ||
      packageBuild?.extraMetadata?.appId,
};

function Modal() {
   const [modalType, setModalType] = useState<string | null>(null);
   const [modalData, setModalData] = useState<Record<string, unknown>>({});
   const [formData, setFormData] = useState<FormState>({});
   const [characterQuery, setCharacterQuery] = useState('');
   const [characters, setCharacters] = useState<Character[]>([]);
   const [imagePreview, setImagePreview] = useState<string | null>(null);
   const [appInfo, setAppInfo] = useState<AppInfo>(fallbackAppInfo);
   const [showDeleteAccount, setShowDeleteAccount] = useState(false);
   const [deleteConfirmation, setDeleteConfirmation] = useState('');
   const [deletePassword, setDeletePassword] = useState('');
   const [deleteError, setDeleteError] = useState<string | null>(null);
   const [isDeletingAccount, setIsDeletingAccount] = useState(false);

   useEffect(() => {
      const handleUIChange = () => {
         setModalType(UIStore.getModalType());
         setModalData(UIStore.getModalData());

         // Initialize form data based on modal type and data
         const type = UIStore.getModalType();
         const data = UIStore.getModalData() as Record<string, unknown>;
         const ids = data as {
            projectId?: string;
            sceneId?: string;
            characterId?: string;
            locationId?: string;
            plotId?: string;
         };

         if (type === 'editProject' && ids.projectId) {
            const project = ProjectStore.getProjects().find((p) => p.id === ids.projectId);
            setFormData({
               ...(project || {}),
            });
            setImagePreview(null);
         } else if (type === 'editScene' && ids.sceneId) {
            const scene = ProjectStore.getScenes().find((s) => s.id === ids.sceneId);
            setFormData({
               ...(scene || {}),
               characterIds: scene?.characterIds || [],
               povCharacterId: scene?.povCharacterId || null,
            });
            setImagePreview(null);
         } else if (type === 'editCharacter' && ids.characterId) {
            const character = ProjectStore.getCharacters().find((c) => c.id === ids.characterId);
            setFormData(character || {});
            setImagePreview(character?.imageUrl || null);
         } else if (type === 'editLocation' && ids.locationId) {
            const location = ProjectStore.getLocations().find((l) => l.id === ids.locationId);
            setFormData(location || {});
            setImagePreview(location?.imageUrl || null);
         } else if (type === 'editPlot' && ids.plotId) {
            const plot = ProjectStore.getPlots().find((p) => p.id === ids.plotId);
            setFormData(plot || {});
            setImagePreview(null);
         } else if (type === 'settings') {
            setFormData({});
            setImagePreview(null);
            setShowDeleteAccount(false);
            setDeleteConfirmation('');
            setDeletePassword('');
            setDeleteError(null);
            setIsDeletingAccount(false);
         } else {
            setFormData({
               characterIds: [],
               povCharacterId: null,
            });
            setImagePreview(null);
         }

         setCharacterQuery('');
      };

      const handleProjectChange = () => {
         setCharacters(ProjectStore.getCharacters());
      };

      handleProjectChange();
      UIStore.addChangeListener(handleUIChange);
      ProjectStore.addChangeListener(handleProjectChange);
      handleUIChange();

      return () => {
         UIStore.removeChangeListener(handleUIChange);
         ProjectStore.removeChangeListener(handleProjectChange);
      };
   }, []);

   useEffect(() => {
      return () => {
         if (imagePreview && imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview);
         }
      };
   }, [imagePreview]);

   useEffect(() => {
      let isMounted = true;
      if (window.electronAPI?.getAppInfo) {
         window.electronAPI
            .getAppInfo()
            .then((info) => {
               if (!isMounted) return;

               setAppInfo({
                  ...fallbackAppInfo,
                  ...info,
                  version: info.version || info.packageVersion || fallbackAppInfo.version,
                  packageVersion: info.packageVersion || info.version || fallbackAppInfo.version,
               });
            })
            .catch((err) => {
               console.warn('Could not load app info', err);
               if (isMounted) setAppInfo(fallbackAppInfo);
            });
      } else if (window.electronAPI?.getAppVersion) {
         window.electronAPI
            .getAppVersion()
            .then((version) => {
               if (isMounted) {
                  setAppInfo({
                     ...fallbackAppInfo,
                     version: version || fallbackAppInfo.version,
                     packageVersion: version || fallbackAppInfo.packageVersion,
                  });
               }
            })
            .catch((err) => {
               console.warn('Could not load app version', err);
               if (isMounted) setAppInfo(fallbackAppInfo);
            });
      } else {
         setAppInfo(fallbackAppInfo);
      }
      return () => {
         isMounted = false;
      };
   }, []);

   const handleClose = () => {
      Actions.hideModal();
      setFormData({});
      if (imagePreview && imagePreview.startsWith('blob:')) {
         URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(null);
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      const user = AuthStore.getUser();
      const projectId = ProjectStore.getSelectedProjectId();
      const projectIdFromModal = modalData.projectId as string | undefined;
      const sceneIdFromModal = modalData.sceneId as string | undefined;
      const characterIdFromModal = modalData.characterId as string | undefined;
      const locationIdFromModal = modalData.locationId as string | undefined;
      const plotIdFromModal = modalData.plotId as string | undefined;

      switch (modalType) {
         case 'newProject':
            if (user?.uid) {
               await Actions.createProject(formData, user.uid);
            }
            break;
         case 'editProject':
            if (projectIdFromModal) {
               await Actions.updateProject(projectIdFromModal, formData);
            }
            break;
         case 'newScene':
            await Actions.createScene(
               { ...formData, order: ProjectStore.getScenes().length },
               projectId,
            );
            break;
         case 'editScene':
            if (sceneIdFromModal) {
               await Actions.updateScene(sceneIdFromModal, formData);
               if (projectId) {
                  // Refresh scenes so linked metadata (e.g., characters) is immediately reflected in the UI.
                  await Actions.loadScenes(projectId);
               }
            }
            break;
         case 'newCharacter':
            await Actions.createCharacter(formData, projectId);
            break;
         case 'editCharacter':
            if (characterIdFromModal) {
               await Actions.updateCharacter(characterIdFromModal, formData);
            }
            break;
         case 'newLocation':
            await Actions.createLocation(formData, projectId);
            break;
         case 'editLocation':
            if (locationIdFromModal) {
               await Actions.updateLocation(locationIdFromModal, formData);
            }
            break;
         case 'newPlot':
            await Actions.createPlot(
               { ...formData, order: ProjectStore.getPlots().length },
               projectId,
            );
            break;
         case 'editPlot':
            if (plotIdFromModal) {
               await Actions.updatePlot(plotIdFromModal, formData);
            }
            break;
         case 'settings':
            handleClose();
            return;
         default:
            break;
      }

      handleClose();
   };

   const handleDeleteAccount = async () => {
      if (deleteConfirmation !== 'DELETE' || !deletePassword || isDeletingAccount) return;

      setDeleteError(null);
      setIsDeletingAccount(true);
      try {
         await Actions.deleteAccount(deletePassword);
         handleClose();
      } catch (error) {
         setDeleteError(
            error instanceof Error ? error.message : 'Account deletion failed. Please try again.',
         );
         setIsDeletingAccount(false);
      }
   };

   const filteredCharacters = useMemo(() => {
      const query = characterQuery.trim().toLowerCase();
      if (!query) return characters;
      return characters.filter((character) => character.name?.toLowerCase().includes(query));
   }, [characterQuery, characters]);

   const addCharacterToScene = (characterId) => {
      if (!characterId) return;
      setFormData((prev) => {
         const existing = prev.characterIds || [];
         if (existing.includes(characterId)) return prev;
         const updated = [...existing, characterId];
         return {
            ...prev,
            characterIds: updated,
            povCharacterId: prev.povCharacterId || characterId,
         };
      });
   };

   const removeCharacterFromScene = (characterId) => {
      setFormData((prev) => {
         const updated = (prev.characterIds || []).filter((id) => id !== characterId);
         const nextPov = prev.povCharacterId === characterId ? null : prev.povCharacterId;
         return {
            ...prev,
            characterIds: updated,
            povCharacterId: updated.includes(nextPov) ? nextPov : null,
         };
      });
   };

   const setPovCharacter = (characterId) => {
      setFormData((prev) => ({
         ...prev,
         povCharacterId: characterId,
      }));
   };

   const renderCharacterChips = () => {
      const selectedIds = formData.characterIds || [];
      if (selectedIds.length === 0) {
         return <p className="text-ash text-xs font-mono">No characters added yet.</p>;
      }

      return (
         <div className="flex flex-wrap gap-2">
            {selectedIds.map((id) => {
               const character = characters.find((c) => c.id === id);
               const name = character?.name || 'Unknown';
               const isPov = formData.povCharacterId === id;
               return (
                  <div
                     key={id}
                     className={`flex items-center space-x-2 px-3 py-2 rounded-full border text-xs font-mono ${
                        isPov
                           ? 'border-amethyst bg-amethyst/10 text-pearl'
                           : 'border-slate/60 text-silver bg-charcoal/40'
                     }`}
                  >
                     <button
                        type="button"
                        onClick={() => setPovCharacter(id)}
                        className={`w-3 h-3 rounded-full border ${isPov ? 'bg-amethyst border-amethyst' : 'border-slate/80'}`}
                        aria-label="Set POV character"
                     />
                     <span>{name}</span>
                     <button
                        type="button"
                        onClick={() => removeCharacterFromScene(id)}
                        className="text-ash hover:text-rose transition-colors"
                        aria-label={`Remove ${name}`}
                     >
                        ×
                     </button>
                  </div>
               );
            })}
         </div>
      );
   };

   const getModalTitle = () => {
      const titles = {
         newProject: 'CREATE NEW PROJECT',
         editProject: 'EDIT PROJECT',
         newScene: 'CREATE NEW SCENE',
         editScene: 'EDIT SCENE',
         newCharacter: 'CREATE NEW CHARACTER',
         editCharacter: 'EDIT CHARACTER',
         newLocation: 'CREATE NEW LOCATION',
         editLocation: 'EDIT LOCATION',
         newPlot: 'CREATE NEW PLOT',
         editPlot: 'EDIT PLOT',
         settings: 'SETTINGS',
      };
      return titles[modalType] || 'MODAL';
   };

   const renderFormFields = () => {
      if (modalType === 'settings') {
         const user = AuthStore.getUser();
         return (
            <div className="space-y-4">
               <div>
                  <h3 className="text-xs font-mono text-ash mb-1">APP</h3>
                  <p className="text-pearl font-mono text-sm">{appInfo.name}</p>
               </div>
               <div>
                  <h3 className="text-xs font-mono text-ash mb-1">USER</h3>
                  <p className="text-pearl font-mono text-sm">{user?.email || 'Unknown user'}</p>
               </div>
               <div>
                  <h3 className="text-xs font-mono text-ash mb-1">THEME</h3>
                  <p className="text-silver text-xs font-mono">Coming soon</p>
               </div>
               <div>
                  <h3 className="text-xs font-mono text-ash mb-1">VERSION</h3>
                  <p className="text-pearl font-mono text-sm">
                     {appInfo.packageVersion || appInfo.version || 'Unknown'}
                  </p>
               </div>
               {appInfo.appId ? (
                  <div>
                     <h3 className="text-xs font-mono text-ash mb-1">BUNDLE ID</h3>
                     <p className="text-pearl font-mono text-sm">{appInfo.appId}</p>
                  </div>
               ) : null}
               {typeof appInfo.isPackaged === 'boolean' ? (
                  <div>
                     <h3 className="text-xs font-mono text-ash mb-1">ENVIRONMENT</h3>
                     <p className="text-pearl font-mono text-sm">
                        {appInfo.isPackaged ? 'Production' : 'Development'}
                     </p>
                  </div>
               ) : null}
               <div className="border-t border-blood/40 pt-4">
                  <h3 className="text-xs font-mono text-rose mb-1">DELETE ACCOUNT</h3>
                  <p className="text-ash text-xs font-mono">
                     Permanently delete your account and all local and synced project data. This
                     cannot be undone.
                  </p>
                  {!showDeleteAccount ? (
                     <button
                        type="button"
                        onClick={() => setShowDeleteAccount(true)}
                        className="mt-3 px-4 py-2 border border-rose/60 text-rose text-xs font-mono rounded hover:bg-blood/20 transition-colors"
                     >
                        DELETE ACCOUNT
                     </button>
                  ) : (
                     <div className="mt-3 space-y-3">
                        <label className="block">
                           <span className="block text-xs font-mono text-silver mb-1">
                              Current password
                           </span>
                           <input
                              type="password"
                              value={deletePassword}
                              onChange={(event) => setDeletePassword(event.target.value)}
                              autoComplete="current-password"
                              className="w-full px-3 py-2 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-rose"
                           />
                        </label>
                        <label className="block">
                           <span className="block text-xs font-mono text-silver mb-1">
                              Type DELETE to confirm
                           </span>
                           <input
                              type="text"
                              value={deleteConfirmation}
                              onChange={(event) => setDeleteConfirmation(event.target.value)}
                              autoComplete="off"
                              className="w-full px-3 py-2 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-rose"
                           />
                        </label>
                        {deleteError ? (
                           <p className="text-rose text-xs font-mono" role="alert">
                              {deleteError}
                           </p>
                        ) : null}
                        <div className="flex gap-2">
                           <button
                              type="button"
                              onClick={() => {
                                 setShowDeleteAccount(false);
                                 setDeleteConfirmation('');
                                 setDeletePassword('');
                                 setDeleteError(null);
                              }}
                              disabled={isDeletingAccount}
                              className="px-4 py-2 bg-charcoal text-silver text-xs font-mono rounded hover:bg-slate disabled:opacity-50"
                           >
                              CANCEL
                           </button>
                           <button
                              type="button"
                              onClick={handleDeleteAccount}
                              disabled={
                                 deleteConfirmation !== 'DELETE' ||
                                 !deletePassword ||
                                 isDeletingAccount
                              }
                              className="px-4 py-2 bg-blood text-white text-xs font-mono rounded hover:bg-rose disabled:opacity-40 disabled:cursor-not-allowed"
                           >
                              {isDeletingAccount ? 'DELETING...' : 'DELETE PERMANENTLY'}
                           </button>
                        </div>
                     </div>
                  )}
               </div>
            </div>
         );
      }

      if (modalType?.includes('Project')) {
         return (
            <>
               <input
                  type="text"
                  placeholder="Project Title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
                  required
               />
               <input
                  type="text"
                  placeholder="Genre (e.g., Fantasy, Sci-Fi)"
                  value={formData.genre || ''}
                  onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
               />
               <textarea
                  placeholder="Project Description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-32 resize-none"
               />
            </>
         );
      }

      if (modalType?.includes('Scene')) {
         return (
            <>
               <input
                  type="text"
                  placeholder="Scene Title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
                  required
               />
               <input
                  type="number"
                  placeholder="Chapter Number"
                  value={formData.chapter || ''}
                  onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
               />
               <input
                  type="text"
                  placeholder="Time (e.g., Morning, Night)"
                  value={formData.time || ''}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
               />
               <textarea
                  placeholder="Scene Description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-32 resize-none"
               />
               <textarea
                  placeholder="Notes"
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-24 resize-none"
               />
               <div className="space-y-2">
                  <label className="text-xs font-mono text-ash">CHARACTERS IN THIS SCENE</label>
                  <div className="relative">
                     <input
                        type="text"
                        placeholder="Type to search characters..."
                        value={characterQuery}
                        onChange={(e) => setCharacterQuery(e.target.value)}
                        className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
                     />
                     {characterQuery && (
                        <div className="absolute z-10 mt-1 w-full bg-midnight border border-charcoal rounded shadow-lg max-h-48 overflow-y-auto">
                           {filteredCharacters.length > 0 ? (
                              filteredCharacters.map((character) => (
                                 <button
                                    key={character.id}
                                    type="button"
                                    onClick={() => {
                                       addCharacterToScene(character.id);
                                       setCharacterQuery('');
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-charcoal/60 text-silver font-mono text-sm"
                                 >
                                    {character.name || 'Unnamed'}
                                 </button>
                              ))
                           ) : (
                              <div className="px-4 py-2 text-ash text-xs font-mono">No matches</div>
                           )}
                        </div>
                     )}
                  </div>
                  {renderCharacterChips()}
                  {(formData.characterIds || []).length > 0 && (
                     <p className="text-ash text-[11px] font-mono">
                        Tip: click the small circle on a chip to mark POV.
                     </p>
                  )}
               </div>
            </>
         );
      }

      if (modalType?.includes('Character')) {
         return (
            <>
               <input
                  type="text"
                  placeholder="Character Name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
                  required
               />
               <input
                  type="text"
                  placeholder="Role (e.g., Protagonist, Antagonist)"
                  value={formData.role || ''}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
               />
               <input
                  type="number"
                  placeholder="Age"
                  value={formData.age || ''}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
               />
               <textarea
                  placeholder="Physical Description"
                  value={formData.appearance || ''}
                  onChange={(e) => setFormData({ ...formData, appearance: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-24 resize-none"
               />
               <textarea
                  placeholder="Personality Traits"
                  value={formData.personality || ''}
                  onChange={(e) => setFormData({ ...formData, personality: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-24 resize-none"
               />
               <textarea
                  placeholder="Backstory"
                  value={formData.backstory || ''}
                  onChange={(e) => setFormData({ ...formData, backstory: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-32 resize-none"
               />
               <div className="space-y-2">
                  <label className="text-xs font-mono text-ash">PROFILE IMAGE</label>
                  <input
                     type="file"
                     accept="image/*"
                     onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (imagePreview && imagePreview.startsWith('blob:')) {
                           URL.revokeObjectURL(imagePreview);
                        }
                        setFormData({ ...formData, imageFile: file });
                        setImagePreview(URL.createObjectURL(file));
                     }}
                     className="w-full text-sm text-silver file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-charcoal file:text-pearl file:cursor-pointer"
                  />
                  {(imagePreview || formData.imageUrl) && (
                     <div className="overflow-hidden rounded border border-charcoal w-full h-40">
                        <img
                           src={imagePreview || formData.imageUrl}
                           alt="Character"
                           className="w-full h-full object-cover"
                        />
                     </div>
                  )}
               </div>
            </>
         );
      }

      if (modalType?.includes('Location')) {
         return (
            <>
               <input
                  type="text"
                  placeholder="Location Name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
                  required
               />
               <input
                  type="text"
                  placeholder="Type (e.g., City, Forest, Building)"
                  value={formData.type || ''}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
               />
               <textarea
                  placeholder="Description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-32 resize-none"
               />
               <textarea
                  placeholder="Notable Features"
                  value={formData.features || ''}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-24 resize-none"
               />
               <div className="space-y-2">
                  <label className="text-xs font-mono text-ash">LOCATION IMAGE</label>
                  <input
                     type="file"
                     accept="image/*"
                     onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (imagePreview && imagePreview.startsWith('blob:')) {
                           URL.revokeObjectURL(imagePreview);
                        }
                        setFormData({ ...formData, imageFile: file });
                        setImagePreview(URL.createObjectURL(file));
                     }}
                     className="w-full text-sm text-silver file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-charcoal file:text-pearl file:cursor-pointer"
                  />
                  {(imagePreview || formData.imageUrl) && (
                     <div className="overflow-hidden rounded border border-charcoal w-full h-40">
                        <img
                           src={imagePreview || formData.imageUrl}
                           alt="Location"
                           className="w-full h-full object-cover"
                        />
                     </div>
                  )}
               </div>
            </>
         );
      }

      if (modalType?.includes('Plot')) {
         return (
            <>
               <input
                  type="text"
                  placeholder="Plot Point Title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
                  required
               />
               <select
                  value={formData.type || ''}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors"
               >
                  <option value="">Select Type</option>
                  <option value="inciting_incident">Inciting Incident</option>
                  <option value="rising_action">Rising Action</option>
                  <option value="climax">Climax</option>
                  <option value="falling_action">Falling Action</option>
                  <option value="resolution">Resolution</option>
               </select>
               <textarea
                  placeholder="Description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-32 resize-none"
               />
               <textarea
                  placeholder="Impact on Story"
                  value={formData.impact || ''}
                  onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-slate/50 rounded text-pearl font-mono text-sm focus:outline-none focus:border-amethyst transition-colors h-24 resize-none"
               />
            </>
         );
      }

      return null;
   };

   if (!modalType) return null;

   return (
      <AnimatePresence>
         <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            {/* Backdrop */}
            <motion.div
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={handleClose}
               className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            />

            {/* Modal */}
            <motion.div
               initial={{ opacity: 0, scale: 0.95, y: 20 }}
               animate={{ opacity: 1, scale: 1, y: 0 }}
               exit={{ opacity: 0, scale: 0.95, y: 20 }}
               transition={{ type: 'spring', damping: 20, stiffness: 300 }}
               className="relative z-10 w-full max-w-lg max-h-[90vh] p-6 bg-midnight border border-charcoal rounded-lg shadow-2xl flex flex-col overflow-hidden"
            >
               {/* Header */}
               <div className="flex items-center justify-between mb-6 flex-shrink-0">
                  <h2 className="text-2xl font-display text-pearl">{getModalTitle()}</h2>
                  <button
                     onClick={handleClose}
                     className="p-2 hover:bg-charcoal rounded transition-colors"
                  >
                     <svg
                        className="w-5 h-5 text-silver"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                  </button>
               </div>

               {/* Form */}
               <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1">
                  {renderFormFields()}

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 flex-shrink-0">
                     <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-2 bg-charcoal text-silver font-display text-lg rounded hover:bg-slate transition-colors"
                     >
                        CANCEL
                     </button>
                     <motion.button
                        type="submit"
                        className="px-6 py-2 bg-amethyst hover:bg-rose text-white font-display text-lg rounded shadow-lg transition-all duration-200"
                     >
                        {modalType === 'settings'
                           ? 'CLOSE'
                           : modalType.includes('new')
                             ? 'CREATE'
                             : 'SAVE'}
                     </motion.button>
                  </div>
               </form>
            </motion.div>
         </div>
      </AnimatePresence>
   );
}

export default Modal;
